import { useState } from 'react';

import { Navigate } from '@tanstack/react-router';

import { ChevronDown, ChevronUp, Copy, Pencil, Plus, Trash2 } from 'lucide-react';

import { Button, Input, Textarea } from '@src/components/ui';
import { useActiveOrg, usePermissions } from '@src/context/hooks';
import {
  useCreateCannedResponseMutation,
  useDeleteCannedResponseMutation,
  useQuery,
  useReorderCannedResponseMutation,
  useUpdateCannedResponseMutation,
} from '@src/query';

import { fillVariables, VARIABLE_TOKENS } from '../Leads/cannedVariables';
import { copyToClipboard } from '../Listings/CopyModalFields';
import { Toaster, useToasts } from '../Listings/useToasts';
import PageHeader from './PageHeader';


const SAMPLE = { buyer_name: 'Alex', part: 'Front bumper', price: '$120', business_name: 'Your yard' };


function Editor({ initial, onSave, onCancel, saving }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [body, setBody] = useState(initial?.body || '');

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-hairline bg-surface-1 p-5">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Still available?)" />

      <Textarea
        value={body}
        rows={4}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Reply text… use {buyer_name}, {part}, {price}, {business_name}"
      />

      <div className="flex flex-wrap gap-1.5">
        {VARIABLE_TOKENS.map((token) => (
          <Button
            key={token}
            variant="secondary"
            className="h-auto rounded-full px-2.5 py-1 text-xs"
            onClick={() => setBody((b) => `${b}{${token}}`)}
          >
            {`{${token}}`}
          </Button>
        ))}
      </div>

      {body
        ? (
          <p className="rounded-lg bg-surface-2 px-3 py-2 text-table text-ink-muted">
            {fillVariables(body, SAMPLE)}
          </p>
        )
        : null}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>

        <Button
          disabled={!title.trim() || !body.trim() || saving}
          loading={saving}
          onClick={() => onSave({ title: title.trim(), body })}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

Editor.displayName = 'Editor';


/** Saved Replies (#268): Owner/Manager-only management of a seller's canned
 * responses — add/edit with a live `fillVariables` preview, up/down reorder,
 * per-row copy, and delete. Non-managers are redirected to the settings hub. */
function SavedReplies() {
  const { seller, membership } = useActiveOrg();
  const { canManageOrg } = usePermissions();
  const { toasts, push } = useToasts();
  const businessName = seller?.name || '';

  const { data = [], isSuccess } = useQuery(`/leads/canned-responses/${seller?.id}/`, { enabled: Boolean(seller) });
  const create = useCreateCannedResponseMutation();
  const update = useUpdateCannedResponseMutation();
  const remove = useDeleteCannedResponseMutation();
  const reorder = useReorderCannedResponseMutation();

  const [editing, setEditing] = useState(null); // 'new' | id | null

  if (membership && !canManageOrg) {
    return <Navigate to="/app/settings" />;
  }

  const sellerId = seller?.id;
  const move = (index, delta) => {
    const ids = data.map((c) => c.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) {
      return; 
    }
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorder.mutate({ sellerId, orderedIds: ids });
  };

  return (
    <div className="flex flex-1 flex-col gap-5 lg:p-7">
      <PageHeader
        title="Saved Replies"
        subtitle="Reusable answers for buyer messages — insert them in Leads or copy for eBay/Facebook."
      />

      <div className="flex w-full max-w-[640px] flex-col gap-3 px-4 pb-6 lg:px-0">
        {editing === 'new'
          ? (
            <Editor
              saving={create.isPending}
              onCancel={() => setEditing(null)}
              onSave={(vals) => create.mutate({ sellerId, ...vals }, { onSuccess: () => setEditing(null) })}
            />
          )
          : (
            <Button className="self-start" onClick={() => setEditing('new')}>
              <Plus className="size-4" />
              {' '}
              Add reply
            </Button>
          )}

        {isSuccess && data.length === 0 && editing !== 'new'
          ? <p className="text-sm text-ink-muted">No saved replies yet.</p>
          : null}

        {data.map((cr, index) => (editing === cr.id
          ? (
            <Editor
              key={cr.id}
              initial={cr}
              saving={update.isPending}
              onCancel={() => setEditing(null)}
              onSave={(vals) => update.mutate({ sellerId, id: cr.id, ...vals }, { onSuccess: () => setEditing(null) })}
            />
          )
          : (
            <div key={cr.id} className="flex items-start gap-3 rounded-lg border border-hairline bg-surface-1 p-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink">{cr.title}</p>
                <p className="mt-0.5 whitespace-pre-wrap text-table text-ink-muted">{cr.body}</p>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  aria-label="Move up"
                  className="size-8 p-0"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  <ChevronUp className="size-4" />
                </Button>

                <Button
                  variant="ghost"
                  aria-label="Move down"
                  className="size-8 p-0"
                  disabled={index === data.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <ChevronDown className="size-4" />
                </Button>

                <Button
                  variant="ghost"
                  aria-label="Copy"
                  className="size-8 p-0"
                  onClick={() => copyToClipboard(fillVariables(cr.body, { business_name: businessName }), push)}
                >
                  <Copy className="size-4" />
                </Button>

                <Button variant="ghost" aria-label="Edit" className="size-8 p-0" onClick={() => setEditing(cr.id)}>
                  <Pencil className="size-4" />
                </Button>

                <Button
                  variant="ghost"
                  aria-label="Delete"
                  className="size-8 p-0 text-danger"
                  onClick={() => remove.mutate({ sellerId, id: cr.id })}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>
          )))}
      </div>

      <Toaster toasts={toasts} />
    </div>
  );
}

SavedReplies.displayName = 'SavedReplies';
export default SavedReplies;
